// En-tête sticky : sélecteur d'exercice permanent + menu utilisateur.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LIBELLE_ROLE, roleEffectif } from "@/lib/roles";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar, LogOut, ChevronDown, UserCircle2 } from "lucide-react";

export function Header() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [exercices, setExercices] = useState([]);
  const [exerciceActif, setExerciceActif] = useState("");

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length > 0) setExerciceActif(String(data[0].annee));
    }).catch(() => setExercices([]));
  }, []);

  const seDeconnecter = async () => {
    await logout();
    navigate("/login");
  };

  // Identité affichée : gérant = nom dérivé de l'entreprise ; invité = nom du compte.
  const role = roleEffectif(user);
  const prenom = role === "gerant" ? company?.prenom_gerant : user?.prenom;
  const nom = role === "gerant" ? company?.nom_gerant : user?.nom;
  const initiale = nom ? `${nom.charAt(0).toUpperCase()}.` : "";
  const identite = prenom ? `${prenom} ${initiale}`.trim() : user?.email;
  const profil = LIBELLE_ROLE[role] || "Utilisateur";

  return (
    <header
      data-testid="entete-application"
      className="no-print h-16 sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#4A8A8A]/20 flex items-center justify-between px-6"
    >
      {/* Sélecteur d'exercice permanent */}
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-[#4A8A8A]" />
        <Select value={exerciceActif} onValueChange={setExerciceActif}>
          <SelectTrigger
            data-testid="selecteur-exercice"
            className="w-[170px] h-9 bg-[#E8F4F4] border-[#4A8A8A]/30 font-semibold text-[#1D3D3D]"
          >
            <SelectValue placeholder="Exercice" />
          </SelectTrigger>
          <SelectContent>
            {exercices.length === 0 ? (
              <SelectItem value="aucun" disabled>Aucun exercice</SelectItem>
            ) : (
              exercices.map((ex) => (
                <SelectItem key={ex.id} value={String(ex.annee)}>
                  Exercice {ex.annee}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Menu utilisateur */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="menu-utilisateur"
            className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#E8F4F4] transition-colors"
          >
            <UserCircle2 className="h-7 w-7 text-[#1D3D3D]" />
            <span className="hidden sm:flex flex-col items-start leading-tight max-w-[200px]">
              <span data-testid="utilisateur-identite" className="text-sm font-semibold text-[#1D3D3D] truncate max-w-[180px]">
                {identite}
              </span>
              <span data-testid="utilisateur-profil" className="text-[11px] text-[#4A8A8A] font-medium">
                {profil}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 text-[#4A8A8A]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col">
            <span className="truncate font-semibold">{identite}</span>
            <span className="text-xs font-normal text-[#4A8A8A]">{profil} · {user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="bouton-deconnexion"
            onClick={seDeconnecter}
            className="text-red-600 focus:text-red-600 cursor-pointer"
          >
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export default Header;
